import React, { useState, useEffect } from 'react';
import { X, User, AlertCircle } from 'lucide-react';
import { User as UserType, Publication } from '../../types';
import { userAPI, publicationAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface ChangeLeadAuthorModalProps {
  isOpen: boolean;
  onClose: () => void;
  publication: Publication | null;
  onSuccess: () => void;
}

const ChangeLeadAuthorModal: React.FC<ChangeLeadAuthorModalProps> = ({
  isOpen,
  onClose,
  publication,
  onSuccess,
}) => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (isOpen && publication) {
      loadUsers();
      setSelectedUserId(null);
      setShowConfirmation(false);
    }
  }, [isOpen, publication]);

  const loadUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const allUsers = await userAPI.getUsers();
      setUsers(allUsers);
    } catch (error: any) {
      toast.error('Failed to load users');
      console.error('Error loading users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedUserId) {
      toast.error('Please select a new lead author');
      return;
    }
    setShowConfirmation(true);
  };

  const handleChangeLeadAuthor = async () => {
    if (!publication || !selectedUserId) {
      return;
    }

    try {
      setIsLoading(true);
      await publicationAPI.changeLeadAuthor(publication.id, selectedUserId);
      toast.success('Lead author changed successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to change lead author');
      console.error('Error changing lead author:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !publication) return null;

  const currentLeadAuthorName = publication.leadAuthorName || publication.lead_author_name || 'Unknown Author';
  const selectedUser = users.find(u => u.id === selectedUserId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Change Lead Author</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4">
          {!showConfirmation ? (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Publication</h3>
                <p className="text-gray-700">{publication.title}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Current Lead Author: <span className="font-medium">{currentLeadAuthorName}</span>
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select New Lead Author <span className="text-red-500">*</span>
                </label>
                {isLoadingUsers ? (
                  <div className="text-center py-4 text-gray-500">Loading users...</div>
                ) : (
                  <select
                    value={selectedUserId || ''}
                    onChange={(e) => setSelectedUserId(parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">-- Select a user --</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.firstName || user.first_name} {user.lastName || user.last_name} ({user.email})
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  The new lead author must already be a user in the system.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!selectedUserId || isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800 mb-2">Confirm Lead Author Change</h3>
                    <p className="text-sm text-yellow-700">
                      You are about to change the lead author for this publication. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6 space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Publication:</p>
                  <p className="text-gray-900 font-medium">{publication.title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current Lead Author:</p>
                  <p className="text-gray-900 font-medium">{currentLeadAuthorName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">New Lead Author:</p>
                  <p className="text-gray-900 font-medium">
                    {selectedUser ? `${selectedUser.firstName || selectedUser.first_name} ${selectedUser.lastName || selectedUser.last_name}` : 'Not selected'}
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handleChangeLeadAuthor}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Changing...</span>
                    </>
                  ) : (
                    <span>Yes, Change Lead Author</span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeLeadAuthorModal;


